import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';

import { AuthStore } from '../auth/auth.store';
import { AuthService } from '../auth/auth.service';

// Refresh compartido entre peticiones: si varias reciben 401 a la vez,
// todas esperan al mismo refresh en vuelo en lugar de disparar N refrescos.
let refreshInFlight: Observable<string> | null = null;

function isAuthEndpoint(url: string): boolean {
  return url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout');
}

function withToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

export const authTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);

  const token = authStore.token();
  const authedRequest = token ? withToken(request, token) : request;

  return next(authedRequest).pipe(
    catchError((error: unknown) => {
      const is401 = error instanceof HttpErrorResponse && error.status === 401;

      // No intentamos refrescar si: no es 401, no hay sesión, o es el propio flujo de auth
      // (evita bucles: un 401 de /auth/refresh significa refresh_ttl agotado).
      if (!is401 || !authStore.token() || isAuthEndpoint(request.url)) {
        return throwError(() => error);
      }

      if (!refreshInFlight) {
        refreshInFlight = authService.refresh().pipe(
          map((response) => response.access_token),
          finalize(() => {
            refreshInFlight = null;
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
      }

      return refreshInFlight.pipe(
        switchMap((newToken) => next(withToken(request, newToken))),
        catchError((refreshError: unknown) => {
          authStore.clearSession();
          void router.navigateByUrl('/login');
          return throwError(() => refreshError);
        })
      );
    })
  );
};
